import { Injectable } from '@angular/core';
import {
  CommonDataParamsService,
  HIERARCHY_ENTITY_TYPES,
} from './common-data-params.service';
import { ScoreUtilsService } from './score-utils.service';
import { TagUtils } from './tag.utils';
import { PortfolioApiService } from '../services/customer-api/portfolio-api.service';
import {
  TopEventsRequestParams,
  BullBearTopEventsRequestParams,
} from '../models/score-events.interface';
import {
  IndustryBenchRequestParams,
  BullBearIndustryBenchRequestParams,
} from '../models/score-time-series.interface';

const RISK_SCORES_WITH_BLACK_SWAN = [
  'general_risk',
  'esg_risk',
  'immediate_risk',
];

const ESG_SCORES_WITH_MATERIALITY_DISABLED = [
  'esg',
  'e_standard',
  's_standard',
  'g_standard',
];

// credit_watch requires explicit risk_sensitivity=0; the Data API behaves
// differently when it's omitted.
const CREDIT_WATCH_RISK_SENSITIVITY = 0;

export type ScoreLabTopEventsParams = Pick<
  TopEventsRequestParams,
  | 'scoreType'
  | 'eventWeights'
  | 'withBlackSwan'
  | 'disableIndustryMateriality'
  | 'riskSensitivity'
>;

export type TopEventsTarget =
  | { kind: 'entity'; type: string; id: string }
  | { kind: 'portfolio'; content: unknown[] };

// Caller may override date resolution either by passing an explicit range
// (`dateFrom`+`dateTo`) or a `time` string; otherwise the service falls back
// to the current URL via `CommonDataParamsService`.
export interface ScoreLabTopEventsInput {
  scoreKey: string;
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  ranking?: 'absolute_count' | 'relative_importance';
  nEvents?: number;
  topEventsSign?: 'neg' | 'pos' | 'all';
  lang?: string[];
  feedTypes?: string[];
}

export interface BullBearTopEventsInput {
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  timeHorizon?: string;
  ranking?: 'absolute_count' | 'relative_importance';
  nEvents?: number;
  topEventsSign?: 'neg' | 'pos' | 'all';
  lang?: string[];
  feedTypes?: string[];
}

export interface IndustryBenchInput {
  scoreKey: string;
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  lang?: string[];
  feedTypes?: string[];
}

export interface BullBearIndustryBenchInput {
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  timeHorizon?: string;
  lang?: string[];
  feedTypes?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class TopEventsParamsService {
  // Defaults inherited from the legacy yukkapi `with_top_events` codepath.
  static readonly BB_TIME_HORIZON = '1M';

  static isBullBearScoreKey(scoreKey: string): boolean {
    return scoreKey === 'bb';
  }

  static buildScoreParams(scoreKey: string): ScoreLabTopEventsParams {
    const scoreType = ScoreUtilsService.resolveScoreKey(scoreKey);
    const params: ScoreLabTopEventsParams = { scoreType };

    if (RISK_SCORES_WITH_BLACK_SWAN.includes(scoreKey)) {
      params.withBlackSwan = true;
    }
    if (ESG_SCORES_WITH_MATERIALITY_DISABLED.includes(scoreKey)) {
      params.disableIndustryMateriality = true;
    }
    if (scoreType === 'credit_watch') {
      params.riskSensitivity = CREDIT_WATCH_RISK_SENSITIVITY;
    }

    return params;
  }

  constructor(
    private commonDataParams: CommonDataParamsService,
    private portfolioApi: PortfolioApiService,
    private scoreUtils: ScoreUtilsService,
  ) {}

  // Custom scores → event weights; predefined → scoreType + flags.
  private resolveScoreParams(scoreKey: string): ScoreLabTopEventsParams {
    const custom = this.scoreUtils.getCustomScoreEventWeights(scoreKey);
    if (custom) {
      return custom.withBlackSwan
        ? { eventWeights: custom.eventWeights, withBlackSwan: true }
        : { eventWeights: custom.eventWeights };
    }
    return TopEventsParamsService.buildScoreParams(scoreKey);
  }

  resolveTarget(): TopEventsTarget {
    const queryParams = this.commonDataParams.getCurrentQueryParams();
    const parsedTag = TagUtils.parse(queryParams.tag);
    if (parsedTag) {
      return { kind: 'entity', type: parsedTag.type, id: parsedTag.id };
    }
    // Single-entity pages carry `type`+`id` — EXCEPT hierarchy drill-downs,
    // which must target the marketGuard constituent portfolio (the Data API
    // cannot score hierarchy participants; docs/refactor-regressions.md #1).
    // `hierarchy` is the market grid's grouping pseudo-type.
    const hierarchyDrilldown =
      HIERARCHY_ENTITY_TYPES.includes(
        queryParams.type as (typeof HIERARCHY_ENTITY_TYPES)[number],
      ) &&
      !!this.portfolioApi.activePortfolio?.marketPortfolio &&
      this.portfolioApi.activePortfolioContent.length > 0;
    if (
      queryParams.type &&
      queryParams.id &&
      queryParams.type !== 'hierarchy' &&
      !hierarchyDrilldown
    ) {
      return { kind: 'entity', type: queryParams.type, id: queryParams.id };
    }
    return {
      kind: 'portfolio',
      content: this.portfolioApi.activePortfolioContent,
    };
  }

  buildScoreLabRequest(input: ScoreLabTopEventsInput): TopEventsRequestParams {
    const { dateFrom, dateTo } = this.resolveDateRange(input);
    const params: TopEventsRequestParams = {
      dateFrom,
      dateTo,
      ranking: input.ranking ?? 'absolute_count',
      nEvents: input.nEvents ?? -1,
      ...this.resolveScoreParams(input.scoreKey),
    };

    if (input.topEventsSign) {
      params.topEventsSign = input.topEventsSign;
    }
    if (input.lang) {
      params.lang = input.lang;
    }
    if (input.feedTypes) {
      params.feedTypes = input.feedTypes;
    }

    return params;
  }

  buildBullBearRequest(
    input: BullBearTopEventsInput,
  ): BullBearTopEventsRequestParams {
    const { dateFrom, dateTo } = this.resolveDateRange(input);
    const params: BullBearTopEventsRequestParams = {
      dateFrom,
      dateTo,
      timeHorizon: input.timeHorizon ?? TopEventsParamsService.BB_TIME_HORIZON,
      ranking: input.ranking ?? 'absolute_count',
      nEvents: input.nEvents ?? -1,
    };

    if (input.topEventsSign) {
      params.topEventsSign = input.topEventsSign;
    }
    if (input.lang) {
      params.lang = input.lang;
    }
    if (input.feedTypes) {
      params.feedTypes = input.feedTypes;
    }

    return params;
  }

  buildIndustryBenchRequest(
    input: IndustryBenchInput,
  ): IndustryBenchRequestParams {
    const { dateFrom, dateTo } = this.resolveDateRange(input);
    const params: IndustryBenchRequestParams = {
      dateFrom,
      dateTo,
      ...this.resolveScoreParams(input.scoreKey),
    };

    if (input.lang) {
      params.lang = input.lang;
    }
    if (input.feedTypes) {
      params.feedTypes = input.feedTypes;
    }

    return params;
  }

  buildBullBearIndustryBenchRequest(
    input: BullBearIndustryBenchInput,
  ): BullBearIndustryBenchRequestParams {
    const { dateFrom, dateTo } = this.resolveDateRange(input);
    const params: BullBearIndustryBenchRequestParams = {
      dateFrom,
      dateTo,
      timeHorizon: input.timeHorizon ?? TopEventsParamsService.BB_TIME_HORIZON,
    };

    if (input.lang) {
      params.lang = input.lang;
    }
    if (input.feedTypes) {
      params.feedTypes = input.feedTypes;
    }

    return params;
  }

  private resolveDateRange(input: {
    time?: string;
    dateFrom?: string;
    dateTo?: string;
  }): { dateFrom: string; dateTo: string } {
    const range = this.commonDataParams.generateTimeRange(input);
    return { dateFrom: range.date_from, dateTo: range.date_to };
  }
}
